import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Menu,
  Pagination
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  DriveFileRenameOutline as SignatureIcon
} from '@mui/icons-material';
import FlowDetails from './FlowDetails';
import ContractDialog from './ContractDialog';
import { useTheme } from '../context/ThemeContext';

function ContractForms() {
  const { themeColors } = useTheme();
  const [contracts, setContracts] = useState([]);
  const [filters, setFilters] = useState({
    id: '',
    documento_id: '',
    nombre: '',
    apellido: '',
    plan: '',
    telefono: ''
  });
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      console.log('Intentando conectar a:', axios.defaults.baseURL);
      const response = await axios.get('/api/contracts');
      console.log('Respuesta:', response.data);
      
      if (Array.isArray(response.data)) {
        setContracts(response.data);
      } else {
        console.warn("Formato inesperado:", response.data);
        setContracts([]);
      }
    } catch (err) {
      console.error("Error detallado:", {
        message: err.message,
        code: err.code,
        response: err.response,
        request: err.request
      });
      
      if (!err.response) {
        alert("Error de conexión. Verificando:\n" +
              "1. ¿El backend está corriendo en puerto 5000?\n" +
              "2. ¿Hay algún error CORS?\n" +
              "3. ¿La URL base es correcta?");
      }
      setContracts([]);
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value
    });
  };

  const filteredContracts = contracts.filter(contract => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return contract[key]?.toString().toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const handleEditClick = async (contract) => {
    try {
      const response = await axios.get(`/api/contracts/${contract.id}`);
      if (response.status === 200) {
        setSelectedContract(response.data);
        setContractDialogOpen(true);
      }
    } catch (error) {
      console.error("Error al obtener datos del contrato:", error);
    }
  };

  const handleDeleteClick = async (contract) => {
    if (window.confirm('¿Está seguro que desea eliminar este contrato?')) {
      try {
        const response = await axios.delete(`/api/contracts/${contract.id}`);
        if (response.status === 200) {
          fetchContracts();
        }
      } catch (error) {
        console.error("Error al eliminar contrato:", error);
        alert("Error al eliminar el contrato: " + error.message);
      }
    }
  };

  const handleContractDialogClose = (wasUpdated) => {
    setContractDialogOpen(false);
    setSelectedContract(null);
    if (wasUpdated) {
      fetchContracts();
    }
  };

  const handlePageChange = (event, newValue) => {
    setCurrentPage(newValue);
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {/* Eliminar estos Tabs duplicados
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab icon={<BarChartIcon />} iconPosition="start" label="Dashboard" />
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Gestión de Contrato" />
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Configuración" />
        </Tabs>
      </Paper>
      */}

      {/* Contenido directo de Gestión de Contrato */}
      <Box>
        {/* Toolbar Superior */}
        <Toolbar sx={{ gap: 2, mb: 2 }}>
          <Select
            size="small"
            value=""
            displayEmpty
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="" disabled>Seleccione una Zona</MenuItem>
            <MenuItem value="zona1">Zona 1</MenuItem>
            <MenuItem value="zona2">Zona 2</MenuItem>
          </Select>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedContract(null);
              setContractDialogOpen(true);
            }}
            sx={{
              backgroundColor: themeColors.primary,
              '&:hover': {
                backgroundColor: themeColors.secondary
              },
              mb: 2
            }}
          >
            + AGREGAR CONTRATO
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Select
            size="small"
            value=""
            displayEmpty
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="" disabled>Acción</MenuItem>
            <MenuItem value="action1">Acción 1</MenuItem>
            <MenuItem value="action2">Acción 2</MenuItem>
          </Select>

          <Button
            variant="contained"
            color="primary"
            disabled
          >
            Ejecutar (0 seleccionados)
          </Button>
        </Toolbar>

        {/* Barra de Herramientas */}
        <Toolbar 
          sx={{ 
            gap: 1, 
            bgcolor: themeColors.primary,
            color: 'white',
            borderRadius: 1,
            mb: 2
          }}
        >
          <Typography>Mostrar 10 registros</Typography>
          <IconButton color="inherit" size="small">
            <RefreshIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Typography>Herramientas</Typography>
        </Toolbar>

        {/* Tabla */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: themeColors.primary,
                '& th': { color: 'white' }
              }}>
                <TableCell padding="checkbox">
                  <input type="checkbox" />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>DNI/C.I/C.C/RIF</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
              {/* Fila de filtros */}
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={filters.id}
                    onChange={handleFilterChange('id')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={filters.documento_id}
                    onChange={handleFilterChange('documento_id')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={filters.nombre}
                    onChange={handleFilterChange('nombre')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={filters.plan}
                    onChange={handleFilterChange('plan')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={filters.telefono}
                    onChange={handleFilterChange('telefono')}
                  />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell padding="checkbox">
                    <input type="checkbox" />
                  </TableCell>
                  <TableCell>{contract.id}</TableCell>
                  <TableCell>{contract.documento_id}</TableCell>
                  <TableCell>{`${contract.nombre} ${contract.apellido}`}</TableCell>
                  <TableCell>{contract.plan}</TableCell>
                  <TableCell>{contract.direccion}</TableCell>
                  <TableCell>{contract.telefono}</TableCell>
                  <TableCell>
                    <Tooltip title="Editar Proceso">
                      <IconButton 
                        size="small"
                        onClick={() => handleEditClick(contract)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton 
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(contract)}
                        sx={{ 
                          color: themeColors.primary,
                          '&:hover': {
                            color: themeColors.secondary
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          justifyContent: 'flex-end',
          '& .MuiPaginationItem-root': {
            color: themeColors.primary,
            '&.Mui-selected': {
              backgroundColor: themeColors.primary,
              color: 'white',
              '&:hover': {
                backgroundColor: themeColors.secondary
              }
            }
          }
        }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>

        {/* Diálogo de Contrato */}
        <ContractDialog
          open={contractDialogOpen}
          onClose={handleContractDialogClose}
          contract={selectedContract}
        />
      </Box>
    </Box>
  );
}

export default ContractForms; 